import {
  BanknotesIcon,
  BellAlertIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CreditCardIcon,
  EyeSlashIcon,
  FilmIcon,
  FunnelIcon,
  GlobeAltIcon,
  HomeIcon,
  InboxIcon,
  LifebuoyIcon,
  MicrophoneIcon,
  PhotoIcon,
  PlayCircleIcon,
  PresentationChartLineIcon,
  SignalIcon,
  TrashIcon,
  UserCircleIcon,
  UsersIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon,
} from "@heroicons/react/24/outline";

export type NavIcon = React.ForwardRefExoticComponent<
  React.SVGProps<SVGSVGElement> & {
    title?: string | undefined;
    titleId?: string | undefined;
  }
>;

export interface NavItem {
  title: string;
  path: string;
  Icon: NavIcon;
  external?: boolean;
  // Extra path prefixes that should highlight this item
  alsoActiveFor?: string[];
}

export interface NavSection {
  // Sections without a title render their items with no heading
  title?: string;
  items: NavItem[];
}

// The pages used daily. Everything else lives in MORE_NAV_ITEMS, behind the
// collapsible "More" group at the bottom of the sidebar.
export const PRIMARY_NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        title: "Users",
        path: "/users",
        Icon: UsersIcon,
        alsoActiveFor: ["/user/"],
      },
      { title: "Support", path: "/support", Icon: InboxIcon },
    ],
  },
  {
    title: "Insights",
    items: [
      { title: "Activity map", path: "/user-activity", Icon: GlobeAltIcon },
      {
        title: "Storefront map",
        path: "/storefront-map",
        Icon: BuildingStorefrontIcon,
      },
      {
        title: "Paid conversion",
        path: "/paid-conversion",
        Icon: BanknotesIcon,
      },
      { title: "Onboarding flow", path: "/onboarding-flow", Icon: FunnelIcon },
      {
        title: "Onboarding analytics",
        path: "/onboarding-analytics",
        Icon: PresentationChartLineIcon,
      },
    ],
  },
  {
    title: "Review",
    items: [
      { title: "Profile photos", path: "/profile-photos", Icon: UserCircleIcon },
      { title: "Public photos", path: "/unverified-photos", Icon: PhotoIcon },
      { title: "Photo verify", path: "/photo-verify", Icon: CheckBadgeIcon },
      {
        title: "Chat messages",
        path: "/chat-messages",
        Icon: ChatBubbleLeftRightIcon,
      },
      { title: "Reels", path: "/reels", Icon: FilmIcon },
      { title: "Live streams", path: "/live-streams", Icon: SignalIcon },
      { title: "Private photos", path: "/private-photos", Icon: EyeSlashIcon },
      {
        title: "Private videos",
        path: "/private-videos",
        Icon: VideoCameraSlashIcon,
      },
    ],
  },
];

export const MORE_NAV_ITEMS: NavItem[] = [
  { title: "Dashboard", path: "/", Icon: HomeIcon },
  { title: "Deleted users", path: "/deleted-users", Icon: TrashIcon },
  { title: "Subscriptions", path: "/subscriptions", Icon: CreditCardIcon },
  { title: "Analytics", path: "/analytics", Icon: ChartBarIcon },
  { title: "Surveys", path: "/surveys", Icon: ClipboardDocumentListIcon },
  { title: "Admin chat", path: "/chat", Icon: ChatBubbleBottomCenterTextIcon },
  {
    title: "Voice recordings",
    path: "/voice-recordings",
    Icon: MicrophoneIcon,
  },
  {
    title: "Reel comments",
    path: "/reel-comments",
    Icon: ChatBubbleOvalLeftEllipsisIcon,
  },
  { title: "Public videos", path: "/unverified-videos", Icon: PlayCircleIcon },
  {
    title: "Video chat status",
    path: "/video-chat-status",
    Icon: VideoCameraIcon,
  },
  {
    title: "Video chat history",
    path: "/video-chat-history",
    Icon: ClockIcon,
  },
  { title: "Push send", path: "/push-send", Icon: BellAlertIcon },
  {
    title: "Helpdesk",
    path: "https://minglify.freshdesk.com/a/dashboard/default",
    Icon: LifebuoyIcon,
    external: true,
  },
];

export const isNavItemActive = (item: NavItem, pathname: string): boolean => {
  if (item.external) return false;
  if (item.path === pathname) return true;
  if (item.path !== "/" && pathname.startsWith(item.path + "/")) return true;
  return (item.alsoActiveFor ?? []).some((prefix) =>
    pathname.startsWith(prefix),
  );
};
